import React from 'react';
import { useShiftSwapData } from './useShiftSwapData';
import { JobDetailsFragment } from '../jobDetailsComponents/graphql';
import { Box, Icon, InfoBadge, IconProps } from '@medely/ui-kit';
import { FragmentType } from '@mp/graphql/generated';

export const ShiftReleaseBadges = ({
  job,
}: {
  job?: FragmentType<typeof JobDetailsFragment> | null;
}) => {
  const {
    proHasSwapRequestToAcceptOrDecline,
    proHasSwapRequestPendingFacility,
    proDidSwapRequest,
    proAwaitingReleaseRequest,
    requestRejected,
    requestExpired,
    canShowShiftReleaseInfo,
    proAwaitingDropRequest,
    proAskedForReleaseButJobIsNotReassigned,
    requestType,
  } = useShiftSwapData({ job });

  if (!canShowShiftReleaseInfo) {
    return null;
  }

  const dropDeclined = requestType === 'drop' && requestRejected;
  const releaseDeclined = requestType === 'release' && requestRejected;
  const swapDeclined = requestType === 'swap' && requestRejected;

  const dropExpired = requestType === 'drop' && requestExpired;
  const releaseExpired = requestType === 'release' && requestExpired;
  const swapExpired = requestType === 'swap' && requestExpired;

  const candidates = [
    {
      condition: proHasSwapRequestToAcceptOrDecline,
      color: 'info' as const,
      label: 'Shift swap',
      iconName: 'light-arrows-repeat' as IconProps['name'],
    },
    {
      condition: dropDeclined,
      color: 'error' as const,
      label: 'Drop declined',
    },
    {
      condition: releaseDeclined,
      color: 'error' as const,
      label: 'Release declined',
    },
    {
      condition: swapDeclined,
      color: 'error' as const,
      label: 'Swap declined',
    },
    {
      condition: dropExpired,
      color: 'error' as const,
      label: 'Drop expired',
    },
    {
      condition: releaseExpired,
      color: 'error' as const,
      label: 'Release expired',
    },
    {
      condition: swapExpired,
      color: 'error' as const,
      label: 'Swap expired',
    },
    {
      condition: proHasSwapRequestPendingFacility,
      color: 'warning' as const,
      label: 'Pending approval',
    },
    {
      condition: proDidSwapRequest,
      color: 'warning' as const,
      label: 'Pending swap',
    },
    {
      condition: proAwaitingDropRequest,
      color: 'warning' as const,
      label: 'Pending drop',
    },
    {
      condition: proAwaitingReleaseRequest,
      color: 'warning' as const,
      label: 'Pending release',
    },
    {
      condition: proAskedForReleaseButJobIsNotReassigned,
      color: 'warning' as const,
      label: 'Release accepted; Pending reassignment',
    },
  ];

  const match = candidates.find((c) => c.condition);

  return match ? (
    <Box alignSelf="flex-start">
      <InfoBadge
        color={match.color}
        label={match.label}
        icon={
          match.iconName ? (
            <Icon
              name={match.iconName}
              variant="custom"
              color="state.info.secondary"
              iconSize="14px"
            />
          ) : null
        }
      />
    </Box>
  ) : null;
};
